import React, { useState } from "react";
import moment from "moment";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import { MdDownload } from "react-icons/md";
import toast from "react-hot-toast";
import PlaceBetServices from "../../../services/PlaceBetServices";

const formatProfitLoss = (pl) => {
  if (!pl) return "—";
  if (pl.type === "EVEN") return "0.00";
  const val = Number(pl.value) || 0;
  const str = `${val.toFixed(2)}`;
  return pl.type === "PROFIT" ? `+${str}` : `-${str}`;
};

/** Remove YT_PLACEBET_ prefix for display/print e.g. YT_PLACEBET_1773031729812_4KKR → 1773031729812_4KKR */
const stripPlaceBetUidPrefix = (val) => {
  if (val == null || val === "") return "—";
  const s = String(val).trim();
  return s.replace(/^YT_PLACEBET_/i, "") || s || "—";
};

const PlaceBetDownload = ({
  search,
  gameType,
  filterType,
  fromDate,
  toDate,
  userId,
  disabled,
}) => {
  const [downloading, setDownloading] = useState(null);
  const api = new PlaceBetServices();

  const fetchAllData = async () => {
    return await api.getPlaceBetListForExport({
      search: search || undefined,
      game_type: gameType || undefined,
      filter_type: filterType || undefined,
      from_date: fromDate || undefined,
      to_date: toDate || undefined,
      userId: userId || undefined,
    });
  };

  const downloadExcel = async () => {
    setDownloading("excel");
    try {
      const data = await fetchAllData();
      if (!data?.length) {
        toast.error("No place bet data to download");
        return;
      }
      const rows = data.map((r, idx) => ({
        "Sr. No.": idx + 1,
        "PlaceBet UID": r.placebet_uid || "-",
        "User Name": r.user_name || "-",
        Session: r.game_type || "-",
        "Session No.": r.session_no ?? "-",
        Amount: Number(r.amount || 0).toFixed(2),
        Winning: r.win_amount != null ? Number(r.win_amount).toFixed(2) : "-",
        "Profit/Loss": formatProfitLoss(r.profit_or_loss),
        Status: r.isDelete
          ? "Cancelled"
          : Number(r.isScan) === 1
            ? "Scanned"
            : Number(r.isScan) === 2
              ? "Expired"
              : r?.role === "Mobile"
                ? "—"
                : "Pending",
        Date: r.created_at
          ? moment.utc(r.created_at).format("DD/MM/YYYY hh:mm A")
          : "-",
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Place Bets");
      const fileName = `place_bets_${fromDate}_to_${toDate}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success("Excel downloaded");
    } catch (err) {
      console.error("Excel download error:", err);
      toast.error(err?.message || "Failed to download Excel");
    } finally {
      setDownloading(null);
    }
  };

  const downloadPDF = async () => {
    setDownloading("pdf");
    try {
      const data = await fetchAllData();
      if (!data?.length) {
        toast.error("No place bet data to download");
        return;
      }
      const doc = new jsPDF({ orientation: "portrait", unit: "mm" });
      const pageWidth = doc.internal.pageSize.getWidth();
      let y = 15;

      doc.setFontSize(14);
      doc.text("Place Bet Management", pageWidth / 2, y, { align: "center" });
      y += 8;
      doc.setFontSize(10);
      doc.text(
        `From: ${fromDate} to To: ${toDate}${gameType ? ` | Session: ${gameType}` : ""}`,
        pageWidth / 2,
        y,
        { align: "center" },
      );
      y += 14;

      const tableData = data.map((r, idx) => [
        idx + 1,
        `${stripPlaceBetUidPrefix(r.placebet_uid)} [#${r.session_no}]`,
        r.user_name || "-",
        r.game_type || "-",
        Number(r.amount || 0).toFixed(2),
        r.win_amount != null ? Number(r.win_amount).toFixed(2) : "-",
        formatProfitLoss(r.profit_or_loss),
        r.isDelete
          ? "Cancelled"
          : Number(r.isScan) === 1
            ? "Scanned"
            : Number(r.isScan) === 2
              ? "Expired"
              : r?.role === "Mobile"
                ? "—"
                : "Pending",
        r.created_at
          ? moment.utc(r.created_at).format("DD/MM/YY hh:mm A")
          : "-",
      ]);

      autoTable(doc, {
        startY: y,
        head: [
          [
            "Sr.",
            "PlaceBet UID",
            "User",
            "Session",
            "Amount",
            "Win",
            "P/L",
            "Status",
            "Date",
          ],
        ],
        body: tableData,
        theme: "grid",
        margin: { left: 14 },
        tableWidth: pageWidth - 28,
        styles: { fontSize: 7 },
        columnStyles: {
          0: { cellWidth: 8 },
          1: { cellWidth: 35 },
          2: { cellWidth: 22 },
          3: { cellWidth: 14 },
          4: { cellWidth: 12 },
          5: { cellWidth: 16 },
          6: { cellWidth: 14 },
          7: { cellWidth: 14 },
          8: { cellWidth: 22 },
        },
      });

      const fileName = `place_bets_${fromDate}_to_${toDate}.pdf`;
      doc.save(fileName);
      toast.success("PDF downloaded");
    } catch (err) {
      console.error("PDF download error:", err);
      toast.error(err?.message || "Failed to download PDF");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="report-download-wrap">
      <div className="report-download-hover-wrap">
        <div className="report-download-trigger">
          <MdDownload size={20} />
        </div>
        <div className="report-download-dropdown">
          <button
            type="button"
            className="report-download-option"
            onClick={downloadExcel}
            disabled={disabled}
            title="Download Excel"
          >
            <MdDownload size={16} />
            <span>{downloading === "excel" ? "Downloading..." : "Excel"}</span>
          </button>
          <button
            type="button"
            className="report-download-option"
            onClick={downloadPDF}
            disabled={disabled}
            title="Download PDF"
          >
            <MdDownload size={16} />
            <span>{downloading === "pdf" ? "Downloading..." : "PDF"}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlaceBetDownload;
